import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Alert, Button, Card, CardBody, Col, Row } from 'reactstrap';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  OBJECTIVE_TYPE_SCOPES,
  ObjectiveType,
  ObjectiveTypeScope,
} from 'utils/models/Objective';
import { getContrastColor } from 'utils/util/color';
import {
  useCurrentOrganization,
  useMe,
  useOrganizationSettings,
} from 'utils/util/hooks';
import { getFriendlyErrorMessageIfErrorOccurred } from 'utils/util/util';
import { sortObjectiveTypesByScopeAndDescendants } from 'views/Objectives/ObjectiveTypeOperations';
import EmptyState from 'views/Widgets/EmptyState';
import { AdvancedCurrentPeopleFilterRoleUserApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import ColorPickerInput from 'views/Widgets/Inputs/ColorPickerInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_ONLY } from 'views/Widgets/People/Filters/common';
import ObjectiveTypeLinkGraph from './ObjectiveTypeLinkGraph';

const INDIVIDUAL_VIRTUAL_ID = -1;

// Scope hierarchy: lower number = higher rank.
// Types can only link to parent types at equal or higher level.
const SCOPE_LEVEL: Record<ObjectiveTypeScope, number> = {
  [OBJECTIVE_TYPE_SCOPES.STRATEGIC]: 0,
  [OBJECTIVE_TYPE_SCOPES.INDIVIDUAL]: 1,
};

/**
 * Normalise a stored JSON value to a valid ApiGroup or undefined.
 * The DB default is `{}` which is not a valid ApiGroup and would crash
 * fromApiFilter, so we treat it (and any other non-GROUP value) as "empty".
 */
const toApiGroupOrUndefined = (
  value: Record<string, unknown> | undefined
): Record<string, unknown> | undefined =>
  value && value.type === 'GROUP' && Array.isArray(value.values)
    ? value
    : undefined;

interface ObjectiveTypesAdminPageProps {
  onCreateRef?: (handler: () => void) => void;
  onDisabledChange?: (disabled: boolean) => void;
}

const ObjectiveTypesAdminPage: FC<ObjectiveTypesAdminPageProps> = ({
  onCreateRef,
  onDisabledChange,
}) => {
  const { formatMessage, formatList } = useIntl();
  const currentOrganization = useCurrentOrganization();
  const me = useMe();
  const settings = useOrganizationSettings();
  const isRestrictedVisibility =
    settings?.objectives_restrict_visibility_to_match_editability ?? false;
  const [objectiveTypes, setObjectiveTypes] = useState<ObjectiveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<ObjectiveType | null>(null);
  const [selectedScope, setSelectedScope] = useState<ObjectiveTypeScope>(
    OBJECTIVE_TYPE_SCOPES.STRATEGIC
  );

  const fetchObjectiveTypes = useCallback(() => {
    if (!currentOrganization?.id) return;
    setIsLoading(true);
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'objective-types',
      { organization: currentOrganization.id },
      (data, _error, hardError) => {
        setIsLoading(false);
        if (hardError || _error) {
          toast.error(
            formatMessage({
              id: 'app.views.administration.objectives_admin.error.load',
              defaultMessage: 'Failed to load objective types.',
            })
          );
          return;
        }
        setObjectiveTypes(data?.results ?? []);
      }
    );
  }, [currentOrganization?.id, formatMessage]);

  useEffect(() => {
    fetchObjectiveTypes();
  }, [fetchObjectiveTypes]);

  const handleCreate = useCallback(() => {
    setEditingType(null);
    setSelectedScope(OBJECTIVE_TYPE_SCOPES.STRATEGIC);
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    onCreateRef?.(handleCreate);
  }, [onCreateRef, handleCreate]);

  useEffect(() => {
    onDisabledChange?.(isRestrictedVisibility);
  }, [onDisabledChange, isRestrictedVisibility]);

  const handleEdit = useCallback((type: ObjectiveType) => {
    setEditingType(type);
    setSelectedScope(type.scope ?? OBJECTIVE_TYPE_SCOPES.INDIVIDUAL);
    setIsModalOpen(true);
  }, []);

  // Stable reference so `ObjectiveTypeLinkGraph`'s React.memo holds and its
  // d3 layout effect (which deps on `onNodeClick`) doesn't re-run on every
  // parent render.
  const handleGraphNodeClick = useCallback(
    (id: number) => {
      const t = objectiveTypes.find((x) => x.id === id);
      if (t) handleEdit(t);
    },
    [objectiveTypes, handleEdit]
  );

  const [deleteTarget, setDeleteTarget] = useState<ObjectiveType | null>(null);

  const handleDelete = useCallback((type: ObjectiveType) => {
    setDeleteTarget(type);
  }, []);

  const dismissDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `objective-types/${deleteTarget.id}`,
      { organization: deleteTarget.organization },
      (_data, error, hardError) => {
        if (hardError || error) {
          toast.error(
            getFriendlyErrorMessageIfErrorOccurred(error, hardError) ||
              formatMessage({
                id: 'app.views.administration.objectives_admin.error.delete',
                defaultMessage: 'Failed to delete objective type.',
              })
          );
          setDeleteTarget(null);
          return;
        }
        setDeleteTarget(null);
        fetchObjectiveTypes();
      }
    );
  }, [deleteTarget, fetchObjectiveTypes, formatMessage]);

  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const handleModalCallback = useCallback(
    (data, _error, _hardError) => {
      if (data) {
        fetchObjectiveTypes();
      }
    },
    [fetchObjectiveTypes]
  );

  const transformObjectBeforeSubmit = useCallback(
    (object: {
      owner?: { id: number } | null;
      administrators?: Record<string, unknown>;
      link_parent_types?: number[];
      [key: string]: unknown;
    }) => {
      return {
        ...object,
        owner: object.owner?.id ?? null,
        administrators: object.administrators ?? {},
        link_parent_types: (object.link_parent_types ?? []).filter(
          (id) => id !== INDIVIDUAL_VIRTUAL_ID
        ),
      };
    },
    []
  );

  const modalObject = useMemo(
    () =>
      editingType
        ? {
            name: editingType.name,
            description: editingType.description,
            scope: editingType.scope ?? OBJECTIVE_TYPE_SCOPES.INDIVIDUAL,
            color: editingType.color ?? '',
            owner: editingType.owner,
            administrators: toApiGroupOrUndefined(editingType.administrators),
            link_parent_types: editingType.link_parent_types ?? [],
            organization: currentOrganization?.id,
          }
        : {
            name: '',
            description: '',
            scope: OBJECTIVE_TYPE_SCOPES.STRATEGIC,
            color: '',
            owner: me,
            administrators: undefined,
            link_parent_types: [],
            organization: currentOrganization?.id,
          },
    [editingType, currentOrganization?.id, me]
  );

  const isIndividualScope =
    editingType?.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL;
  const showAdministrators = selectedScope === OBJECTIVE_TYPE_SCOPES.STRATEGIC;

  const LinkParentTypesInput: FC<{
    value: number[];
    onChange: (value: number[]) => void;
  }> = useCallback(
    ({ value, onChange }) => {
      const editingId = editingType?.id ?? null;
      const isEditingIndividual =
        editingType?.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL;
      const currentLevel = SCOPE_LEVEL[selectedScope] ?? 999;
      const availableTypes = sortObjectiveTypesByScopeAndDescendants(
        objectiveTypes.filter(
          (t) => (SCOPE_LEVEL[t.scope] ?? 999) <= currentLevel
        )
      );
      if (availableTypes.length === 0) {
        return (
          <p className="text-muted mb-0">
            <FormattedMessage
              id="app.views.administration.objectives_admin.form.link_parent_types_empty"
              defaultMessage="No other objective types available. Create more types to configure parent linking."
            />
          </p>
        );
      }
      return (
        <div>
          {availableTypes.map((t) => {
            const isSelfLink = isEditingIndividual && t.id === editingId;
            const isChecked = isSelfLink || (value ?? []).includes(t.id);
            return (
              <div key={t.id} className="form-check mb-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`link-parent-type-${t.id}`}
                  checked={isChecked}
                  disabled={isSelfLink}
                  onChange={() => {
                    if (isSelfLink) return;
                    const updated = isChecked
                      ? (value ?? []).filter((id) => id !== t.id)
                      : [...(value ?? []), t.id];
                    onChange(updated);
                  }}
                />
                <label
                  className="form-check-label d-inline-flex align-items-center gap-2"
                  htmlFor={`link-parent-type-${t.id}`}
                  style={isSelfLink ? { opacity: 0.5 } : undefined}
                >
                  {t.color ? (
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor: t.color,
                        color: getContrastColor(t.color),
                      }}
                    >
                      {t.name}
                    </span>
                  ) : (
                    <span className="badge rounded-pill bg-primary-soft">
                      {t.name}
                    </span>
                  )}
                </label>
              </div>
            );
          })}
        </div>
      );
    },
    [objectiveTypes, editingType, selectedScope]
  );

  const modalInputs = useMemo(
    () =>
      [
        !isIndividualScope
          ? {
              required: true,
              name: 'name',
              label: formatMessage({
                id: 'app.views.administration.objectives_admin.form.name',
                defaultMessage: 'Name',
              }),
              placeholder: formatMessage({
                id: 'app.views.administration.objectives_admin.form.name_placeholder',
                defaultMessage: 'e.g. Company Objective, Initiative',
              }),
              type: INPUT_TYPES.TEXT,
              ignorePasswordManagers: true,
              autoFocus: true,
              maxLength: 100,
            }
          : null,
        !isIndividualScope
          ? {
              required: false,
              name: 'description',
              label: formatMessage({
                id: 'app.views.administration.objectives_admin.form.description',
                defaultMessage: 'Description',
              }),
              placeholder: formatMessage({
                id: 'app.views.administration.objectives_admin.form.description_placeholder',
                defaultMessage: 'Describe the purpose of this objective type',
              }),
              type: INPUT_TYPES.TEXT,
              inputType: 'textarea',
            }
          : null,
        !isIndividualScope
          ? {
              required: true,
              name: 'color',
              label: formatMessage({
                id: 'app.views.administration.objectives_admin.form.color',
                defaultMessage: 'Color',
              }),
              helperText: formatMessage({
                id: 'app.views.administration.objectives_admin.form.color_help',
                defaultMessage:
                  'Choose a color for this objective type badge. Select a preset or use the color picker.',
              }),
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              component: ColorPickerInput,
            }
          : null,
        !isIndividualScope
          ? {
              required: false,
              name: 'owner',
              label: formatMessage({
                id: 'app.views.administration.objectives_admin.form.owner',
                defaultMessage: 'Owner',
              }),
              type: INPUT_TYPES.SELECT_PERSON,
              clearable: false,
              disabled: true,
            }
          : null,
        showAdministrators
          ? {
              required: false,
              name: 'administrators',
              label: formatMessage({
                id: 'app.views.administration.objectives_admin.form.administrators',
                defaultMessage: 'Administrators',
              }),
              helperText: formatMessage({
                id: 'app.views.administration.objectives_admin.form.administrators_help',
                defaultMessage:
                  "Administrators can edit objectives of this type. Authority cascades to descendant types: an administrator here is automatically an administrator of any type that has this one as an ancestor through 'Allowed parent types'. Leave empty to make this type pickable for everyone when creating new objectives. Edit rights then come only from being a collaborator.",
              }),
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              component: AdvancedCurrentPeopleFilterRoleUserApiComponent,
            }
          : null,
        {
          required: false,
          name: 'link_parent_types',
          label: formatMessage({
            id: 'app.views.administration.objectives_admin.form.link_parent_types',
            defaultMessage: 'Allowed parent types',
          }),
          helperText: formatMessage({
            id: 'app.views.administration.objectives_admin.form.link_parent_types_help',
            defaultMessage:
              'Select which objective types can be set as parents for objectives of this type. Administrators of any selected parent type also administer this type by inheritance, so this list shapes both the hierarchy users can build and the admin authority that flows down.',
          }),
          type: INPUT_TYPES.CUSTOM_COMPONENT,
          component: LinkParentTypesInput,
        },
      ].filter(Boolean),
    [formatMessage, isIndividualScope, showAdministrators, LinkParentTypesInput]
  );

  const sortedTypes = useMemo(
    () => sortObjectiveTypesByScopeAndDescendants(objectiveTypes),
    [objectiveTypes]
  );

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.objectives_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        format: (value, _uniqueId, _rowIndex, row) => (
          <span
            className="text-nowrap d-inline-block ps-2 py-2"
            style={{
              borderLeft: row.color
                ? `4px solid ${row.color}`
                : '4px solid transparent',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.objectives_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        renderIfEmpty: true,
        format: (value) =>
          value || (
            <em className="text-muted">
              {formatMessage({
                id: 'app.views.administration.objectives_admin.table.no_description',
                defaultMessage: 'No description',
              })}
            </em>
          ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.objectives_admin.table.owner',
          defaultMessage: 'Owner',
        }),
        field: 'owner',
        format: FORMAT_AVATAR_ONLY,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.objectives_admin.table.administrators',
          defaultMessage: 'Administrators',
        }),
        field: 'administrators',
        renderIfEmpty: true,
        format: (value) =>
          value === null
            ? '—'
            : value?.values?.length
              ? formatMessage(
                  {
                    id: 'app.views.administration.objectives_admin.table.filter_count',
                    defaultMessage:
                      '{count, plural, one {# filter} other {# filters}}',
                  },
                  { count: value.values.length }
                )
              : formatMessage({
                  id: 'app.views.administration.objectives_admin.table.everyone',
                  defaultMessage: 'Everyone',
                }),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.objectives_admin.table.parent_types',
          defaultMessage: 'Parent types',
        }),
        field: 'link_parent_types',
        renderIfEmpty: true,
        format: (value) => {
          if (!value || value.length === 0) {
            return <span className="text-muted">{'—'}</span>;
          }
          const names = value
            .map(
              (typeId: number) =>
                objectiveTypes.find((ot) => ot.id === typeId)?.name
            )
            .filter(Boolean);
          return (
            <span>
              {formatList(names, { type: 'disjunction', style: 'long' })}
            </span>
          );
        },
      },
      {
        name: '',
        field: 'actions',
        sortable: false,
        renderIfEmpty: true,
        format: (_value, _uniqueId, _rowIndex, row) => (
          <div className="text-end text-nowrap">
            <Button
              color="white"
              size="sm"
              className="me-2"
              disabled={isRestrictedVisibility}
              title={formatMessage({
                id: 'app.views.administration.objectives_admin.table.edit',
                defaultMessage: 'Edit',
              })}
              onClick={() => handleEdit(row)}
            >
              <i className="fe fe-edit-2" />
            </Button>
            <Button
              color="white"
              size="sm"
              disabled={
                isRestrictedVisibility ||
                row.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
              }
              title={formatMessage({
                id: 'app.views.administration.objectives_admin.table.delete',
                defaultMessage: 'Delete',
              })}
              onClick={() => handleDelete(row)}
            >
              <i className="fe fe-trash" />
            </Button>
          </div>
        ),
      },
    ],
    [
      formatMessage,
      formatList,
      handleEdit,
      handleDelete,
      objectiveTypes,
      isRestrictedVisibility,
    ]
  );

  if (isLoading) {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.objectives_admin.loading',
          defaultMessage: 'Loading objective types...',
        })}
      />
    );
  }

  return (
    <>
      <ConfirmationDialogModal
        isOpen={!!deleteTarget}
        toggle={dismissDelete}
        onClosed={dismissDelete}
        confirmCallback={confirmDelete}
        confirmationButtonColor="danger"
        title={formatMessage({
          id: 'app.views.administration.objectives_admin.delete_modal.title',
          defaultMessage: 'Delete objective type',
        })}
        description={
          <FormattedMessage
            id="app.views.administration.objectives_admin.delete_modal.description"
            defaultMessage='Are you sure you want to delete the objective type "{name}"? This action cannot be undone.'
            values={{ name: deleteTarget?.name }}
          />
        }
        confirmText={formatMessage({
          id: 'app.views.administration.objectives_admin.delete_modal.confirm',
          defaultMessage: 'Delete',
        })}
      />
      {isRestrictedVisibility && (
        <Alert color="info" className="mb-3">
          <i className="fe fe-info me-2" />
          <FormattedMessage
            id="app.views.administration.objectives_admin.restricted_visibility_notice"
            defaultMessage="Objective types cannot be modified because objectives visibility is set to <b>hierarchical (chain-of-command)</b>. To manage objective types, switch to full visibility."
            values={{ b: (chunks) => <strong>{chunks}</strong> }}
          />
        </Alert>
      )}
      <Card>
        <CardBody>
          {sortedTypes.length === 0 ? (
            <EmptyState
              title={formatMessage({
                id: 'app.views.administration.objectives_admin.empty.title',
                defaultMessage: 'No objective types',
              })}
              subtitle={formatMessage({
                id: 'app.views.administration.objectives_admin.empty.subtitle',
                defaultMessage:
                  'Define objective categories for your organization (e.g. Company Objective, Initiative). Each type can control creation and editing permissions.',
              })}
            >
              <Button
                color="primary"
                disabled={isRestrictedVisibility}
                onClick={handleCreate}
              >
                <i className="fe fe-layers me-1" />
                <FormattedMessage
                  id="app.views.administration.objectives_admin.create_type"
                  defaultMessage="Create type"
                />
              </Button>
            </EmptyState>
          ) : (
            <>
              <Row className="mb-3">
                <Col>
                  <p className="text-muted mb-1">
                    <FormattedMessage
                      id="app.views.administration.objectives_admin.empty.subtitle"
                      defaultMessage="Define objective categories for your organization (e.g. Company Objective, Initiative). Each type can control creation and editing permissions."
                    />{' '}
                    <i
                      id="scope-hierarchy-help"
                      className="fe fe-help-circle text-primary"
                      style={{ cursor: 'pointer' }}
                    />
                    <SafeUncontrolledPopover
                      placement="bottom"
                      target="scope-hierarchy-help"
                      trigger="hover legacy"
                      delay={{ show: 100, hide: 300 }}
                    >
                      <div className="p-2">
                        <p className="fw-bold mb-4">
                          <FormattedMessage
                            id="app.views.administration.objectives_admin.scope_hierarchy.title"
                            defaultMessage="Scope hierarchy"
                          />
                        </p>
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span className="badge bg-light text-dark border">
                            <FormattedMessage
                              id="app.views.administration.objectives_admin.scope_hierarchy.strategic"
                              defaultMessage="Strategic"
                            />
                          </span>
                          <i className="fe fe-arrow-up text-muted" />
                          <span className="badge bg-light text-dark border">
                            <FormattedMessage
                              id="app.views.administration.objectives_admin.scope_hierarchy.level1"
                              defaultMessage="Individual"
                            />
                          </span>
                        </div>
                        <p className="text-muted small mt-2 mb-0">
                          <FormattedMessage
                            id="app.views.administration.objectives_admin.scope_hierarchy.description"
                            defaultMessage="Types can only link to parents at the same level or above."
                          />
                        </p>
                      </div>
                    </SafeUncontrolledPopover>
                  </p>
                  <p className="text-muted small mb-0">
                    <FormattedMessage
                      id="app.views.administration.objectives_admin.types.cascade_tagline"
                      defaultMessage="Administrators of a type also administer every type that descends from it through 'Allowed parent types'."
                    />
                  </p>
                </Col>
              </Row>
              <ObjectiveTypeLinkGraph
                objectiveTypes={sortedTypes}
                onNodeClick={handleGraphNodeClick}
              />
              <FilterablePeopleTable
                rows={sortedTypes}
                columns={columns}
                hideFilters={true}
                hideExportButton={true}
                hideHeader={true}
                showTable={true}
                showToggleFilters={false}
              />
            </>
          )}
        </CardBody>
      </Card>

      <ModalEditor
        isOpen={isModalOpen}
        toggle={toggleModal}
        title={
          editingType
            ? formatMessage({
                id: 'app.views.administration.objectives_admin.modal.edit_title',
                defaultMessage: 'Edit objective type',
              })
            : formatMessage({
                id: 'app.views.administration.objectives_admin.modal.create_title',
                defaultMessage: 'Create objective type',
              })
        }
        submitText={formatMessage({
          id: 'app.views.administration.objectives_admin.form.save',
          defaultMessage: 'Save',
        })}
        method={
          editingType && editingType.id !== INDIVIDUAL_VIRTUAL_ID
            ? 'PATCH'
            : 'POST'
        }
        url={
          editingType && editingType.id !== INDIVIDUAL_VIRTUAL_ID
            ? `objective-types/${editingType.id}`
            : 'objective-types'
        }
        object={modalObject}
        inputs={modalInputs}
        callback={handleModalCallback}
        transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      />
    </>
  );
};

export default React.memo(ObjectiveTypesAdminPage);
